/* eslint-disable no-console */
import { toRaw } from "vue";
import { defineStore } from "pinia";
import Base from "+/Engine/Base";
import Entity from "+/Engine/Entity";

class EntityStore extends Base {
  static #stores = {};
  #namespace;
  #entityType;
  #entityDefinition;

  constructor(namespace, entityType, entityDefinition) {
    super();
    this.#namespace = namespace;
    this.#entityType = entityType;
    this.#entityDefinition = entityDefinition;
    if (!EntityStore.#stores[namespace]) {
      const store = defineStore(namespace, {
        state: () => ({
          entities: {},
        }),

        getters: {
          getRawEntity: state => {
            return pkString => {
              return toRaw(state.entities[pkString]);
            };
          },
          hasEntity: state => {
            return pkString => {
              if (state.entities[pkString]) {
                return true;
              }
              return false;
            };
          },
          getEntity: state => {
            return pkString => {
              return state.entities[pkString];
            };
          },
          getEntityFieldData: state => {
            return pkString => {
              return state.entities[pkString]?.fieldData || {};
            };
          },
          getEntityField: state => {
            return (pkString, fieldName) => {
              if (!state.entities[pkString]) {
                return null;
              }
              return state.entities[pkString].fieldData[fieldName];
            };
          },
        },

        actions: {
          updateEntity(pkString, fields) {
            if (!this.entities[pkString]) {
              throw new Error(`Missing entity: ${pkString}`);
            }
            // console.log(`updating entity ${entityType}(${pkString})`);
            Object.keys(fields).forEach(fieldName => {
              this.entities[pkString].fieldData[fieldName] = fields[fieldName];
            });
          },
        },
      });
      EntityStore.#stores[namespace] = store();
    }
  }

  getStore() {
    return EntityStore.#stores[this.#namespace];
  }

  get #myStore() {
    return EntityStore.#stores[this.#namespace];
  }

  getEntityField(pkString, fieldName) {
    if (typeof pkString !== "string") {
      throw new Error("EntityStore getEntity requires pkString to be a string");
    }
    return this.#myStore.getEntityField(pkString, fieldName);
  }

  hasEntity(pkString) {
    return this.#myStore.hasEntity(pkString);
  }

  getAllEntities() {
    // Filter out null/undefined entries before calling `Entity.fromJson`.
    // Pinia stores can briefly hold tombstone/sparse slots — typically
    // when a delete fires concurrently with a render pass that still
    // iterates the entity map — and `Entity.fromJson(undefined)` throws
    // "Cannot read properties of undefined (reading 'appName')" because
    // it dereferences `json.appName` synchronously.
    return Object.values(this.#myStore.entities)
        .filter(entity => entity != null)
        .map(entity => Entity.fromJson(entity));
  }

  getEntity(pkString) {
    if (typeof pkString !== "string") {
      throw new Error("EntityStore getEntity requires pkString to be a string");
    }
    const entity = this.#myStore.getEntity(pkString);
    // The underlying Pinia store returns undefined for entities that
    // have been deleted or never existed. Returning null here lets
    // callers (DynamicElement.element, edit-border-mixin's
    // parent-chain walk) follow the existing optional-chaining
    // pattern instead of throwing "Cannot read properties of
    // undefined (reading 'appName')" from Entity.fromJson when a
    // delete races with a render pass that still references the
    // tombstoned id.
    if (entity == null) {
      return null;
    }
    return Entity.fromJson(entity);
  }

  getEntityFieldData(pkString) {
    if (typeof pkString !== "string") {
      throw new Error("EntityStore getEntity requires pkString to be a string");
    }

    return this.#myStore.getEntityFieldData(pkString);
  }

  updateEntity(pkString, fieldsToUpdate) {
    if (!this.hasEntity(pkString)) {
      throw new Error("you cannot update an entity that is not already stored");
    }

    Object.keys(fieldsToUpdate).forEach(fieldName => {
      this.#assertValidFieldValue(fieldName, fieldsToUpdate[fieldName]);
    });

    this.#myStore.updateEntity(pkString, fieldsToUpdate);
  }

  upsertEntity(pkString, entity) {
    if (!this.hasEntity(pkString)) {
      return this.storeEntity(pkString, entity);
    }

    this.#assertValidEntityStructure(entity);

    const fieldsToUpdate = {};
    const entityJson = this.#myStore.getRawEntity(pkString);
    const basicData = entityJson.fieldData;
    Object.keys(basicData).forEach(fieldName => {
      // manyEntities values live exclusively in FeedStore — never mirrored onto the entity.
      // See Engine README "Invariant: entities never store values for their manyEntities fields".
      if (entity.getFieldType(fieldName) === "manyEntities") {
        return;
      }
      if (entity[fieldName] !== basicData[fieldName]) {
        fieldsToUpdate[fieldName] = entity[fieldName];
      }
    });
    this.updateEntity(pkString, fieldsToUpdate);
  }

  storeEntity(pkString, entity) {
    if (typeof pkString !== "string") {
      throw new Error("EntityStore storeEntity requires pkString to be a string");
    }
    if (entity.constructor.name !== "Entity") {
      throw new Error("EntityStore storeEntity requires entity to be an Entity");
    }

    this.#assertValidEntityStructure(entity);

    console.log(`storing entity ${this.#entityType}(${pkString})`, entity);
    const entityJson = entity.toJson();
    Object.keys(entityJson.fieldData).forEach(fieldName => {
      console.log(` -- setting ${fieldName} = ${JSON.stringify(entityJson.fieldData[fieldName])}`);
    });

    this.#myStore.entities[pkString] = entityJson;
  }

  removeEntity(pkString) {
    if (typeof pkString !== "string") {
      throw new Error("EntityStore storeEntity requires pkString to be a string");
    }
    if (!this.hasEntity(pkString)) {
      throw new Error(`EntityStore storeEntity does not contain entity ${pkString}`);
    }

    delete this.#myStore.entities[pkString];
  }

  #assertValidEntityStructure(entity) {
    const entityFields = entity.getBasicFieldData();
    Object.keys(entityFields).forEach(fieldName => {
      this.#assertValidFieldValue(fieldName, entityFields[fieldName]);
    });
  }

  // eslint-disable-next-line no-unused-vars
  #assertValidFieldValue(fieldName, fieldValue) {
    // TODO: fieldValue type checking
    if (!this.#entityDefinition.fields[fieldName]) {
      throw new Error(`EntityStore storeEntity fieldName not defined on Entity for ${this.#entityType} - [fieldName -> ${fieldName}]`);
    }
  }
}
export default EntityStore;
